/**
 * analysisUtils.js — Utility functions extracted from Analysis.jsx
 * Provides scale color mapping, demographic ordering, and data aggregation
 */
import { humanizeColumnName } from './formatters';

// Semantic color palettes — aligned with SEMANTIC_HEX in lib/colors.js (teal/coral/slate)
export const SCALE_COLORS = {
  sentiment_5: ['#fda4af', '#FCD34D', '#94A3B8', '#5eead4', '#0d9488'],
  sentiment_7: ['#f87171', '#fda4af', '#FCD34D', '#94A3B8', '#a7f3d0', '#5eead4', '#0d9488'],
  binary_positive_negative: ['#5eead4', '#fda4af'],
  binary_with_neutral: ['#5eead4', '#fda4af', '#94A3B8'],
  generic: ['#5eead4', '#0d9488', '#FCD34D', '#a7f3d0', '#fda4af', '#93C5FD', '#3B82F6', '#94A3B8', '#67E8F9'],
};

export const COLORS = SCALE_COLORS.generic;
export const ROUND_COLORS = ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#eff6ff', '#f1f5f9', '#cbd5e1', '#94a3b8', '#64748b'];

const NEGATIVE_KEYWORDS = ['strongly disagree', 'disagree', 'very unfavorable', 'unfavorable', 'very unlikely', 'unlikely', 'oppose', 'strongly oppose', 'no', 'poor', 'very poor', 'dissatisfied', 'very dissatisfied'];
const POSITIVE_KEYWORDS = ['strongly agree', 'agree', 'very favorable', 'favorable', 'very likely', 'likely', 'support', 'strongly support', 'yes', 'excellent', 'very good', 'good', 'satisfied', 'very satisfied'];
const NEUTRAL_KEYWORDS = ['neutral', 'uncertain', 'neither', 'undecided', 'maybe', 'average', 'fair'];

// --- Palette Definitions ---

// Diverging: for polarized scales (agree/disagree, favorable/unfavorable)
// Aligned with semantic palette: coral (negative) → slate (neutral) → teal (positive)
const PALETTE_DIVERGING = {
  3: ['#ef4444', '#94a3b8', '#0d9488'],
  4: ['#ef4444', '#fda4af', '#5eead4', '#0d9488'],
  5: ['#ef4444', '#fda4af', '#94a3b8', '#5eead4', '#0d9488'],
  7: ['#dc2626', '#ef4444', '#fda4af', '#94a3b8', '#5eead4', '#14b8a6', '#0d9488'],
};

// Sequential: for neutral ordinal scales (low→high, frequency, intensity)
const PALETTE_SEQUENTIAL = {
  2: ['#BFDBFE', '#1D4ED8'],
  3: ['#BFDBFE', '#60A5FA', '#1D4ED8'],
  4: ['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8'],
  5: ['#DBEAFE', '#93C5FD', '#60A5FA', '#3B82F6', '#1D4ED8'],
  7: ['#EFF6FF', '#DBEAFE', '#93C5FD', '#60A5FA', '#3B82F6', '#2563EB', '#1D4ED8'],
};

// Categorical: for non-ordered categories (distinct hues, semantic-aligned)
const PALETTE_CATEGORICAL = [
  '#3B82F6', '#0d9488', '#F59E0B', '#0891b2',
  '#ef4444', '#06B6D4', '#0e7490', '#14B8A6',
  '#2563eb', '#D97706', '#0d9488', '#dc2626',
];

/**
 * Detect scale type from response options and optional hint
 * @param {string[]} responseScale
 * @param {string} [scaleTypeHint] - from stimulus.scale_type (e.g. 'likert_5', 'custom')
 * @returns {'diverging'|'sequential'|'categorical'}
 */
export const detectScaleType = (responseScale, scaleTypeHint) => {
  if (!responseScale || responseScale.length === 0) return 'categorical';

  // If scale_type hint says likert or favorability/likelihood → diverging
  if (scaleTypeHint) {
    const h = scaleTypeHint.toLowerCase();
    if (h.includes('likert') || h === 'favorability' || h === 'likelihood') return 'diverging';
    if (h === 'yes_no' || h === 'yes_no_maybe') return 'diverging';
  }

  const lowers = responseScale.map(s => s.toLowerCase().trim());

  // Count how many options match positive/negative keywords
  let negCount = 0, posCount = 0, neutralCount = 0;
  for (const lower of lowers) {
    if (NEGATIVE_KEYWORDS.some(k => lower.includes(k))) negCount++;
    else if (POSITIVE_KEYWORDS.some(k => lower.includes(k))) posCount++;
    else if (NEUTRAL_KEYWORDS.some(k => lower.includes(k))) neutralCount++;
  }

  // If both poles are represented → diverging
  if (negCount >= 1 && posCount >= 1) return 'diverging';

  // Check for ordinal patterns (numbered scales, frequency, level-based)
  const ordinalPatterns = [
    /^[1-9]$/, /^[1-9]\d?$/, // numeric: 1, 2, 3...
    /low|medium|high|moderate|extreme/i,
    /never|rarely|sometimes|often|always/i,
    /none|some|most|all/i,
    /very\s+(low|little|few)/i,
    /slightly|moderately|considerably|extremely/i,
    /minimal|limited|substantial|extensive/i,
  ];
  const ordinalMatches = lowers.filter(l => ordinalPatterns.some(p => p.test(l))).length;
  if (ordinalMatches >= Math.ceil(lowers.length * 0.5)) return 'sequential';

  // Default: if 2-3 options with no polarity → could be categorical
  if (responseScale.length <= 3 && negCount === 0 && posCount === 0 && neutralCount === 0) return 'categorical';

  // Fallback: if we can't determine, use diverging for Likert-like (5-7 options) else categorical
  if (responseScale.length >= 4 && responseScale.length <= 7) return 'sequential';
  return 'categorical';
};

/**
 * Get palette array for a given number of options and scale type
 */
const getPalette = (type, count) => {
  if (type === 'diverging') {
    if (PALETTE_DIVERGING[count]) return PALETTE_DIVERGING[count];
    return interpolatePalette(PALETTE_DIVERGING[7] || PALETTE_DIVERGING[5], count);
  }
  if (type === 'sequential') {
    if (PALETTE_SEQUENTIAL[count]) return PALETTE_SEQUENTIAL[count];
    return interpolatePalette(PALETTE_SEQUENTIAL[7] || PALETTE_SEQUENTIAL[5], count);
  }
  // categorical
  return PALETTE_CATEGORICAL.slice(0, Math.max(count, 2));
};

function interpolatePalette(base, count) {
  if (count <= base.length) return base.slice(0, count);
  const result = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.round((i / (count - 1)) * (base.length - 1));
    result.push(base[Math.min(idx, base.length - 1)]);
  }
  return result;
}

/**
 * Scale-type-aware color map: assigns colors to response options based on scale type
 * @param {string[]} responseScale - ordered response options
 * @param {string} [scaleTypeHint] - from stimulus.scale_type
 * @returns {Object} map of response → hex color
 */
export const getScaleColorsMap = (responseScale, scaleTypeHint) => {
  if (!responseScale || responseScale.length === 0) return {};
  const type = detectScaleType(responseScale, scaleTypeHint);
  const ordered = responseScale; // already ordered by caller
  const palette = getPalette(type, ordered.length);
  const colorMap = {};
  for (let i = 0; i < ordered.length; i++) {
    colorMap[ordered[i]] = palette[i % palette.length];
  }
  return colorMap;
};

export const getScaleColors = (responseScale, scaleTypeHint) => {
  if (!responseScale || responseScale.length === 0) return SCALE_COLORS.generic;
  const colorMap = getScaleColorsMap(responseScale, scaleTypeHint);
  return responseScale.map(opt => colorMap[opt] || '#94A3B8');
};

/**
 * Round percentages to integers that sum exactly to 100 (largest remainder method)
 * @param {Object} percentages - { 'option': rawPct, ... }
 * @returns {Object} { 'option': roundedInt, ... }
 */
export const roundToHundred = (percentages) => {
  if (!percentages) return {};
  const entries = Object.entries(percentages);
  if (entries.length === 0) return {};
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) return Object.fromEntries(entries.map(([k]) => [k, 0]));
  // Normalize to 100
  const normalized = entries.map(([k, v]) => ({ key: k, raw: (v / total) * 100 }));
  const floored = normalized.map(e => ({ ...e, floor: Math.floor(e.raw), remainder: e.raw - Math.floor(e.raw) }));
  let currentSum = floored.reduce((s, e) => s + e.floor, 0);
  // Sort by largest remainder descending
  const sorted = [...floored].sort((a, b) => b.remainder - a.remainder);
  let idx = 0;
  while (currentSum < 100 && idx < sorted.length) {
    sorted[idx].floor++;
    currentSum++;
    idx++;
  }
  const result = {};
  for (const e of floored) result[e.key] = e.floor;
  return result;
};

export const getOrderedScale = (responseScale) => {
  if (!responseScale || responseScale.length === 0) return [];
  const scored = responseScale.map((option, originalIndex) => {
    const lower = option.toLowerCase().trim();
    let score = 50;
    if (NEGATIVE_KEYWORDS.some(k => lower.includes(k))) { score = (lower.includes('strongly') || lower.includes('very')) ? 10 : 30; }
    else if (POSITIVE_KEYWORDS.some(k => lower.includes(k))) { score = (lower.includes('strongly') || lower.includes('very')) ? 90 : 70; }
    else if (NEUTRAL_KEYWORDS.some(k => lower.includes(k))) { score = 50; }
    return { option, score, originalIndex };
  });
  scored.sort((a, b) => a.score - b.score);
  return scored.map(s => s.option);
};

export const getOrderedDemographicValues = (values, columnName) => {
  if (!values || values.length === 0) return [];
  const lowerName = columnName?.toLowerCase() || '';
  const extractNumber = (str) => {
    const s = str.toLowerCase().trim();
    if (s.startsWith('<')) return parseInt(s.match(/\d+/)?.[0] || '0') - 0.5;
    if (s.startsWith('>') || s.endsWith('+')) return parseInt(s.match(/\d+/)?.[0] || '999') + 0.5;
    const match = s.match(/(\d+)/);
    return match ? parseInt(match[1]) : 999;
  };
  if (lowerName.includes('age') || lowerName.includes('età') || lowerName.includes('fascia'))
    return [...values].sort((a, b) => extractNumber(a) - extractNumber(b));
  if (lowerName.includes('tenure') || lowerName.includes('experience') || lowerName.includes('years') || lowerName.includes('anni') || lowerName.includes('service') || lowerName.includes('anzianità') || lowerName.includes('seniority') || lowerName.includes('permanenza'))
    return [...values].sort((a, b) => extractNumber(a) - extractNumber(b));
  if (lowerName.includes('education') || lowerName.includes('degree') || lowerName.includes('istruzione') || lowerName.includes('titolo') || lowerName.includes('studio') || lowerName.includes('qualification')) {
    const order = ['none','nessuno','elementary','primary','elementare','primaria','middle','secondary','media','secondaria','high school','diploma','superiore','maturità','some college','associate','vocational','bachelor','laurea','triennale','undergraduate','master','magistrale','specialistica','graduate','mba','phd','doctorate','dottorato','postgraduate'];
    return [...values].sort((a, b) => { const iA = order.findIndex(e => a.toLowerCase().includes(e)); const iB = order.findIndex(e => b.toLowerCase().includes(e)); if (iA !== -1 && iB !== -1) return iA - iB; if (iA !== -1) return -1; if (iB !== -1) return 1; return a.localeCompare(b); });
  }
  if (lowerName.includes('level') || lowerName.includes('livello') || lowerName.includes('grade') || lowerName.includes('position') || lowerName.includes('role') || lowerName.includes('ruolo') || lowerName.includes('rank') || lowerName.includes('job')) {
    const order = ['intern','stagista','trainee','apprentice','entry','junior','associate','staff','mid','intermediate','professional','senior','specialist','expert','lead','team lead','principal','manager','supervisor','head','senior manager','director','direttore','senior director','vp','vice president','svp','evp','c-level','executive','coo','cfo','cto','cmo','ceo','president','owner'];
    return [...values].sort((a, b) => { const iA = order.findIndex(e => a.toLowerCase().includes(e)); const iB = order.findIndex(e => b.toLowerCase().includes(e)); if (iA !== -1 && iB !== -1) return iA - iB; if (iA !== -1) return -1; if (iB !== -1) return 1; return a.localeCompare(b); });
  }
  if (lowerName.includes('income') || lowerName.includes('salary') || lowerName.includes('compensation') || lowerName.includes('stipendio') || lowerName.includes('reddito') || lowerName.includes('pay') || lowerName.includes('wage') || lowerName.includes('retribuzione'))
    return [...values].sort((a, b) => parseInt(a.replace(/[^0-9]/g, '').slice(0, 6) || '0') - parseInt(b.replace(/[^0-9]/g, '').slice(0, 6) || '0'));
  if (lowerName.includes('department') || lowerName.includes('function') || lowerName.includes('dipartimento') || lowerName.includes('funzione') || lowerName.includes('area') || lowerName.includes('division'))
    return [...values].sort((a, b) => a.localeCompare(b));
  if (lowerName.includes('region') || lowerName.includes('location') || lowerName.includes('regione') || lowerName.includes('sede') || lowerName.includes('country') || lowerName.includes('city'))
    return [...values].sort((a, b) => a.localeCompare(b));
  const hasNum = values.some(v => /^[<>]?\d+[-–]?\d*\+?$|^\d+\s*(years?|anni|mesi|months?)/.test(v.trim().toLowerCase()));
  if (hasNum) return [...values].sort((a, b) => extractNumber(a) - extractNumber(b));
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
};

export const aggregateNumericValues = (breakdownData, columnName, maxGroups = 8) => {
  if (!breakdownData || Object.keys(breakdownData).length <= maxGroups) return breakdownData;
  const lowerName = columnName?.toLowerCase() || '';
  const isNum = lowerName.includes('age') || lowerName.includes('età') || lowerName.includes('year') || lowerName.includes('anni') || lowerName.includes('tenure') || lowerName.includes('experience') || lowerName.includes('salary') || lowerName.includes('income');
  if (!isNum) return breakdownData;
  const keys = Object.keys(breakdownData);
  const numKeys = keys.filter(k => /^\d+(\.\d+)?$/.test(k.trim()));
  if (numKeys.length < keys.length * 0.7) return breakdownData;
  const nums = numKeys.map(k => parseFloat(k)).sort((a, b) => a - b);
  const min = Math.min(...nums), max = Math.max(...nums), span = max - min;
  let rangeSize;
  if (lowerName.includes('age')) rangeSize = 10;
  else if (span <= 10) rangeSize = 2;
  else if (span <= 30) rangeSize = 5;
  else if (span <= 50) rangeSize = 10;
  else rangeSize = Math.ceil(span / 6);
  const start = Math.floor(min / rangeSize) * rangeSize;
  const agg = {};
  for (let s = start; s <= max; s += rangeSize) agg[`${s}-${s + rangeSize - 1}`] = { percentages: {}, count: 0 };
  for (const [key, data] of Object.entries(breakdownData)) {
    const n = parseFloat(key);
    if (isNaN(n)) continue;
    const rk = `${Math.floor(n / rangeSize) * rangeSize}-${Math.floor(n / rangeSize) * rangeSize + rangeSize - 1}`;
    if (!agg[rk]) agg[rk] = { percentages: {}, count: 0 };
    const cur = agg[rk].count || 0, nc = data.count || 1, tot = cur + nc;
    for (const [r, p] of Object.entries(data.percentages || {})) agg[rk].percentages[r] = ((agg[rk].percentages[r] || 0) * cur + p * nc) / tot;
    agg[rk].count = tot;
  }
  const result = {};
  Object.keys(agg).filter(k => agg[k].count > 0).sort((a, b) => parseInt(a) - parseInt(b)).forEach(k => { result[k] = agg[k]; });
  return result;
};

/**
 * Compute variance bands for each response option across all demographic groups
 * Returns { [response]: { mean, min, max, std } }
 */
export const computeVarianceBands = (allBreakdowns, orderedResponseScale) => {
  if (!allBreakdowns || orderedResponseScale.length === 0) return null;
  const bands = {};
  for (const resp of orderedResponseScale) {
    const values = [];
    for (const demoData of Object.values(allBreakdowns)) {
      if (!demoData?.breakdown) continue;
      for (const grp of Object.values(demoData.breakdown)) {
        if (grp?.percentages?.[resp] !== undefined) values.push(grp.percentages[resp]);
      }
    }
    if (values.length < 2) { bands[resp] = { mean: values[0] || 0, min: values[0] || 0, max: values[0] || 0, std: 0 }; continue; }
    const mean = values.reduce((s, v) => s + v, 0) / values.length;
    const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
    bands[resp] = { mean, min: Math.min(...values), max: Math.max(...values), std: Math.sqrt(variance) };
  }
  return bands;
};
